const BalanceSheetTable = ({ data = [], totalDebits = 0, totalCredits = 0 }) => {
  const formatPrice = (price) => {
  const absPrice = Math.abs(price || 0);
    return `Rp ${new Intl.NumberFormat("id-ID", {
      style: "decimal",
      minimumFractionDigits: 2,
    }).format(absPrice)}`;
  };

  // Group accounts by main category - Match format like 1.0.00, 1.1.00, 1.1.01, etc
  const grouped = {
    "Aktiva Lancar": data.filter(
      (acc) => {
        const code = acc.account_code;
        // Match: 1.0.xx, 1.00.xx, 1.01.xx, 1.1.xx, 1.10.xx, 1.11.xx, etc
        // Pattern: starts with "1." then 0 or 1 (with optional leading zero), then anything
        return code === "1.0.00" || code === "1.00" || code === "1.0" ||
               code === "1.1.00" || code === "1.1" || code === "1.1.0" ||
               code.match(/^1\.0\d?\./) ||   // 1.0x.xx or 1.00.xx or 1.01.xx
               code.match(/^1\.1\d?\./) ||   // 1.1x.xx or 1.10.xx or 1.11.xx  
               code.match(/^1\.0\d$/);       // 1.01, 1.02, etc (without third segment)
      }
    ),
    "Aktiva Tetap": data.filter(
      (acc) => {
        const code = acc.account_code;
        return code === "1.2.00" || code === "1.2" || code === "1.2.0" ||
               code.match(/^1\.2\d?\./) ||   // 1.2x.xx or 1.20.xx
               code.match(/^1\.2\d$/);       // 1.21, 1.22, etc
      }
    ),
    Hutang: data.filter((acc) => acc.account_code.startsWith("2")),
    Modal: data.filter((acc) => acc.account_code.startsWith("3")),
  };

  // Calculate totals per group with all columns
  const totalPerGroup = (items) => {
    const debit = items.reduce((sum, i) => sum + (i.debit_balance || 0), 0);
    const credit = items.reduce((sum, i) => sum + (i.credit_balance || 0), 0);
    
    // For closing balance: debit - credit
    const closing = debit - credit;
    
    return { debit, credit, closing };
  };

  const totalAktivaLancar = totalPerGroup(grouped["Aktiva Lancar"]);
  const totalAktivaTetap = totalPerGroup(grouped["Aktiva Tetap"]);
  const totalAktiva = {
    debit: totalAktivaLancar.debit + totalAktivaTetap.debit,
    credit: totalAktivaLancar.credit + totalAktivaTetap.credit,
    closing: totalAktivaLancar.closing + totalAktivaTetap.closing,
  };
  
  const totalHutang = totalPerGroup(grouped.Hutang);
  const totalModal = totalPerGroup(grouped.Modal);
  
  // For Pasiva (Hutang & Modal), closing = credit - debit (opposite of Aktiva)
  const totalPassiva = {
    debit: totalHutang.debit + totalModal.debit,
    credit: totalHutang.credit + totalModal.credit,
    closing: (totalHutang.credit - totalHutang.debit) + (totalModal.credit - totalModal.debit),
  };

  // Check if Balance Sheet is balanced (Total Aktiva = Total Pasiva)
  const isBalanced = Math.abs(totalAktiva.closing - totalPassiva.closing) < 0.01;
  const difference = totalAktiva.closing - totalPassiva.closing;

  // ALSO check if backend data is balanced (total_debits = total_credits from API)
  const isBackendBalanced = Math.abs(totalDebits - totalCredits) < 0.01;
  const backendDifference = totalDebits - totalCredits;

  // Final check: both frontend calculation and backend must be balanced
  const isTrulyBalanced = isBalanced && isBackendBalanced;

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border border-collapse text-sm">
        <thead className="bg-blue-900 text-white sticky top-0">
          <tr>
            <th className="p-3 border text-center" colSpan="2" rowSpan="2">Perkiraan</th>
            <th className="p-3 border text-center">Saldo Awal</th>
            <th className="p-3 border text-center">Debit</th>
            <th className="p-3 border text-center">Kredit</th>
            <th className="p-3 border text-center">Saldo Akhir</th>
          </tr>
        </thead>
        <thead className="bg-blue-800 text-white sticky top-12">
          <tr>
            <th className="p-2 border text-center w-[12%]">Kode</th>
            <th className="p-2 border text-left">Nama Perkiraan</th>
            <th className="p-2 border text-right w-[14%]">Rp</th>
            <th className="p-2 border text-right w-[14%]">Rp</th>
            <th className="p-2 border text-right w-[14%]">Rp</th>
            <th className="p-2 border text-right w-[14%]">Rp</th>
          </tr>
        </thead>

        <tbody>
          {/* AKTIVA LANCAR */}
          <tr className="bg-gradient-to-r from-blue-50 to-blue-100 font-semibold">
            <td colSpan="6" className="p-3 border text-blue-900">
              <i className="mgc_folder_open_line me-2"></i>Aktiva Lancar
            </td>
          </tr>
          {grouped["Aktiva Lancar"].map((acc, idx) => (
            <tr key={idx} className="hover:bg-blue-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">0.00</td>
              <td className="p-2 border text-right">{formatPrice(acc.debit_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.credit_balance || 0)}</td>
              <td className="p-2 border text-right font-semibold">
                {formatPrice((acc.debit_balance || 0) - (acc.credit_balance || 0))}
              </td>
            </tr>
          ))}
          <tr className="bg-blue-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Aktiva Lancar</td>
            <td className="p-2 border text-right">0.00</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaLancar.debit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaLancar.credit)}</td>
            <td className="p-2 border text-right font-bold">{formatPrice(totalAktivaLancar.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1 bg-gray-50"></td></tr>

          {/* AKTIVA TETAP */}
          <tr className="bg-gradient-to-r from-blue-50 to-blue-100 font-semibold">
            <td colSpan="6" className="p-3 border text-blue-900">
              <i className="mgc_building_2_line me-2"></i>Aktiva Tetap
            </td>
          </tr>
          {grouped["Aktiva Tetap"].map((acc, idx) => (
            <tr key={idx} className="hover:bg-blue-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">0.00</td>
              <td className="p-2 border text-right">{formatPrice(acc.debit_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.credit_balance || 0)}</td>
              <td className="p-2 border text-right font-semibold">
                {formatPrice((acc.debit_balance || 0) - (acc.credit_balance || 0))}
              </td>
            </tr>
          ))}
          <tr className="bg-blue-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Aktiva Tetap</td>
            <td className="p-2 border text-right">0.00</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaTetap.debit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaTetap.credit)}</td>
            <td className="p-2 border text-right font-bold">{formatPrice(totalAktivaTetap.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1 bg-gray-50"></td></tr>

          {/* TOTAL AKTIVA */}
          <tr className="bg-gradient-to-r from-blue-300 to-blue-400 text-blue-900 font-bold text-base">
            <td colSpan="2" className="p-3 border text-right uppercase">
              <i className="mgc_chart_line me-2"></i>Total Aktiva
            </td>
            <td className="p-3 border text-right">0.00</td>
            <td className="p-3 border text-right">{formatPrice(totalAktiva.debit)}</td>
            <td className="p-3 border text-right">{formatPrice(totalAktiva.credit)}</td>
            <td className="p-3 border text-right font-bold text-lg">{formatPrice(totalAktiva.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-2 bg-gray-100"></td></tr>

          {/* HUTANG */}
          <tr className="bg-gradient-to-r from-red-50 to-red-100 font-semibold">
            <td colSpan="6" className="p-3 border text-red-900">
              <i className="mgc_wallet_3_line me-2"></i>Hutang / Liabilitas
            </td>
          </tr>
          {grouped.Hutang.map((acc, idx) => (
            <tr key={idx} className="hover:bg-red-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">0.00</td>
              <td className="p-2 border text-right">{formatPrice(acc.debit_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.credit_balance || 0)}</td>
              <td className="p-2 border text-right font-semibold">
                {formatPrice((acc.credit_balance || 0) - (acc.debit_balance || 0))}
              </td>
            </tr>
          ))}
          <tr className="bg-red-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Hutang</td>
            <td className="p-2 border text-right">0.00</td>
            <td className="p-2 border text-right">{formatPrice(totalHutang.debit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalHutang.credit)}</td>
            <td className="p-2 border text-right font-bold">{formatPrice(totalHutang.credit - totalHutang.debit)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1 bg-gray-50"></td></tr>

          {/* MODAL */}
          <tr className="bg-gradient-to-r from-green-50 to-green-100 font-semibold">
            <td colSpan="6" className="p-3 border text-green-900">
              <i className="mgc_safe_box_line me-2"></i>Modal / Ekuitas
            </td>
          </tr>
          {grouped.Modal.map((acc, idx) => (
            <tr key={idx} className="hover:bg-green-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">0.00</td>
              <td className="p-2 border text-right">{formatPrice(acc.debit_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.credit_balance || 0)}</td>
              <td className="p-2 border text-right font-semibold">
                {formatPrice((acc.credit_balance || 0) - (acc.debit_balance || 0))}
              </td>
            </tr>
          ))}
          <tr className="bg-green-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Modal</td>
            <td className="p-2 border text-right">0.00</td>
            <td className="p-2 border text-right">{formatPrice(totalModal.debit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalModal.credit)}</td>
            <td className="p-2 border text-right font-bold">{formatPrice(totalModal.credit - totalModal.debit)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1 bg-gray-50"></td></tr>

          {/* TOTAL PASSIVA */}
          <tr className="bg-gradient-to-r from-purple-300 to-purple-400 text-purple-900 font-bold text-base">
            <td colSpan="2" className="p-3 border text-right uppercase">
              <i className="mgc_chart_bar_line me-2"></i>Total Pasiva
            </td>
            <td className="p-3 border text-right">0.00</td>
            <td className="p-3 border text-right">{formatPrice(totalPassiva.debit)}</td>
            <td className="p-3 border text-right">{formatPrice(totalPassiva.credit)}</td>
            <td className="p-3 border text-right font-bold text-lg">
              {formatPrice(totalPassiva.credit - totalPassiva.debit)}
            </td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-2 bg-gray-100"></td></tr>

          {/* Balance Check Indicator */}
          {isTrulyBalanced ? (
            <tr className="bg-green-100">
              <td colSpan="6" className="p-3 border text-center text-green-700 font-bold">
                ✓ Balance Sheet: BALANCED (Total Aktiva = Total Pasiva)
              </td>
            </tr>
          ) : (
            <tr className="bg-red-100">
              <td colSpan="6" className="p-3 border text-center text-red-700 font-bold">
                ✗ Balance Sheet: NOT BALANCED
                <br />
                {!isBalanced && (
                  <span className="text-sm">Aktiva vs Pasiva Difference: {formatPrice(Math.abs(difference))}</span>
                )}
                {!isBackendBalanced && (
                  <span className="text-sm block">
                    Backend Data Unbalanced: Total Debits ({formatPrice(totalDebits)}) ≠ Total Credits ({formatPrice(totalCredits)}) 
                    <br />Difference: {formatPrice(Math.abs(backendDifference))}
                  </span>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default BalanceSheetTable;
